import AccountMappingApproval from "./AccountMappingApproval";
import DomainSourceUpload from "./DomainSourceUpload";
import { useCallback, useEffect, useState } from "react";
import api, { rupiah, getErrorMessage, downloadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Receipt, FileDown } from "lucide-react";

const STATUS_STYLE = {
  RECONCILED: "bg-green-100 text-green-800 border-green-300",
  APPROVED: "bg-green-100 text-green-800 border-green-300",
  PASS: "bg-green-100 text-green-800 border-green-300",
  NO_FINDINGS: "bg-green-100 text-green-800 border-green-300",
  PASS_WITH_REVIEW_ITEMS: "bg-amber-100 text-amber-800 border-amber-300",
  REVIEW_REQUIRED: "bg-amber-100 text-amber-800 border-amber-300",
  AMBIGUOUS: "bg-red-100 text-red-800 border-red-300",
  BLOCKED_SOURCE: "bg-red-100 text-red-800 border-red-300",
  BLOCKED_MAPPING: "bg-red-100 text-red-800 border-red-300",
  BLOCKED_PERIOD: "bg-red-100 text-red-800 border-red-300",
  SOURCE_NOT_SUPPLIED: "bg-slate-100 text-slate-700 border-slate-300",
};
const ACCEPTANCE_LABELS = {
  PASS: "Diterima",
  PASS_WITH_REVIEW_ITEMS: "Diterima dengan pos reviu",
  BLOCKED_SOURCE: "Tertahan — sumber belum tersedia",
  BLOCKED_MAPPING: "Tertahan — pemetaan akun pajak belum disetujui",
  BLOCKED_PERIOD: "Tertahan — periode belum lengkap",
};
const ITEM_LABELS = {
  ACTIVE: "Aktif", DUPLICATE: "Duplikat", SUPERSEDED: "Versi lama",
  OUT_OF_PERIOD: "Di luar periode", INVALID: "Tidak valid", REVIEW_REQUIRED: "Perlu reviu",
  RECONCILED: "Cocok", AMOUNT_DIFFERENCE: "Perbedaan nilai", GL_ONLY: "Hanya di buku besar",
  WHT_ONLY: "Hanya di WHT",
  MATCHED_EXACT: "Cocok — referensi dokumen entitas",
  MATCHED_MULTI_ATTRIBUTE: "Cocok — beberapa atribut",
  WHT_WITHOUT_SOURCE_TRANSACTION: "Tanpa transaksi sumber",
  COUNTERPARTY_MISMATCH: "Lawan transaksi berbeda",
  DPP_DIFFERENCE: "Perbedaan DPP", TIMING_DIFFERENCE: "Perbedaan waktu",
  MATCHED_BILLING: "Cocok dengan billing",
  PAYABLE_WITHOUT_BILLING: "Kewajiban tanpa billing", BILLING_WITHOUT_PAYABLE: "Billing tanpa kewajiban",
  PERIOD_DIFFERENCE: "Perbedaan masa", MATCHED_PAYMENT: "Cocok dengan pembayaran",
  UNPAID_BILLING: "Billing belum dibayar", PARTIAL_PAYMENT: "Dibayar sebagian",
  DUPLICATE_PAYMENT: "Pembayaran ganda", PAYMENT_WITHOUT_BILLING: "Pembayaran tanpa billing",
  MATCHED_BANK_STRONG_REFERENCE: "Cocok dengan bank (referensi billing)",
  BANK_PAYMENT_NOT_FOUND: "Tidak ditemukan di bank",
  SOURCE_NOT_SUPPLIED: "Tidak berlaku / sumber tidak tersedia",
};
const NOT_AVAILABLE = "Tidak berlaku / sumber tidak tersedia";

const TABS = [
  ["ringkasan", "Ringkasan", () => true],
  ["populasi", "Populasi WHT", (v) => v.population?.controls?.active > 0],
  ["pph21", "PPh 21", (v) => (v.by_article || {})["PPh 21"]],
  ["pph23", "PPh 23", (v) => (v.by_article || {})["PPh 23"]],
  ["pphfinal", "PPh Final 4(2)", (v) => (v.by_article || {})["PPh 4(2)"]],
  ["payroll", "Payroll PPh 21", (v) => v.pph21_branches?.PAYROLL?.count > 0],
  ["vsgl", "WHT vs GL", (v) => !!v.wht_vs_gl],
  ["dpp", "DPP / Transaksi Sumber", (v) => !!v.source_transactions],
  ["billing", "Billing Pajak", (v) => !!v.payable_vs_billing],
  ["pembayaran", "Pembayaran Pajak", (v) => !!v.billing_vs_payment],
  ["bank", "Pembayaran vs Bank", (v) => !!v.payment_vs_bank],
  ["rollforward", "Roll-forward Bulanan", (v) => !!v.roll_forward],
  ["kualitas", "Pos Rekonsiliasi", (v) => !!v.data_quality],
  ["bukti", "Bukti Audit", () => true],
];

// Pajak Dipotong/Dipungut (WHT) — populasi, buku besar, penyetoran dan roll-forward bulanan.
export default function WithholdingTax({ id }) {
  const [view, setView] = useState(null);
  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState("ringkasan");

  const load = useCallback(() => {
    api.get(`/engagements/${id}/wht`).then(({ data }) => setView(data))
      .catch((e) => toast.error(getErrorMessage(e)));
    api.get(`/engagements/${id}/wht-summary`).then(({ data }) => setSummary(data))
      .catch(() => setSummary(null));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const exportWorkpaper = () =>
    downloadFile(`/engagements/${id}/wht/workpaper.xlsx`, `kertas-kerja-wht-${id}.xlsx`)
      .catch((e) => toast.error(getErrorMessage(e)));

  if (!view) return <div className="text-xs text-muted-foreground p-4">Memuat data WHT…</div>;
  const available = TABS.filter(([, , when]) => when(view));

  return (
    <div className="space-y-4" data-testid="withholding-tax">
      <DomainSourceUpload id={id} endpoint="wht-sources" label="Unggah arsip atau berkas PPh (nama berkas dan elemen XML menentukan jenisnya)" accept=".zip,.xlsx,.xls,.xml,.pdf" onUploaded={load} />
      <AccountMappingApproval id={id} path="wht-accounts" title="Pemetaan akun hutang PPh per pasal" testid="wht-mapping-approval" onApproved={load}
        articles={["PPh 21", "PPh 22", "PPh 23", "PPh 26", "PPh 4(2)"]} />
      <div className="bg-white border border-border rounded-sm p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 font-head font-semibold text-sm mr-2">
          <Receipt size={15} />Pajak Dipotong / Dipungut
        </div>
        <div className="text-xs text-muted-foreground">{view.audit_period_display}</div>
        <Button size="sm" variant="outline" className="ml-auto" onClick={exportWorkpaper}
          data-testid="wht-export">
          <FileDown size={14} className="mr-1" />Ekspor Kertas Kerja
        </Button>
      </div>

      {summary && (
        <div className="bg-white border border-border rounded-sm p-2" data-testid="wht-domain-summary">
          <div className="text-xs font-head font-semibold px-1 pb-2">
            Ringkasan Domain WHT
            <span className={`ml-2 font-normal inline-block px-2 py-0.5 border rounded-sm ${STATUS_STYLE[summary.acceptance] || ""}`}>
              {ACCEPTANCE_LABELS[summary.acceptance] || summary.acceptance}
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <Figure label="Arsip sumber" value={`${summary.sources} arsip · ${summary.archive_members} berkas`} />
            <Figure label="Catatan aktif" value={summary.records_active} />
            <Figure label="Dasar pengenaan pajak" value={rupiah(summary.tax_base)} />
            <Figure label="Pajak dipotong" value={rupiah(summary.tax_amount)} />
            <Figure label="Nilai dinyatakan sumber" value={summary.records_with_a_stated_tax_amount} />
            <Figure label="Nilai hasil perhitungan" value={summary.records_with_a_computed_tax_amount} />
            <Figure label="PPh 21 bukti potong" value={summary.pph21_bupot_records} />
            <Figure label="PPh 21 penggajian" value={summary.pph21_payroll_records} />
            <Figure label="Orang pada kedua sumber" value={summary.pph21_overlap} />
            <Figure label="Pemetaan akun" value={summary.account_mapping_status} status={summary.account_mapping_status} />
            <Figure label="WHT ↔ buku besar" value={statusText(summary.wht_vs_gl_status)} status={summary.wht_vs_gl_status} />
            <Figure label="Kewajiban ↔ billing" value={statusText(summary.payable_vs_billing_status)} status={summary.payable_vs_billing_status} />
            <Figure label="Billing ↔ pembayaran" value={statusText(summary.billing_vs_payment_status)} status={summary.billing_vs_payment_status} />
            <Figure label="Pembayaran ↔ bank" value={statusText(summary.payment_vs_bank_status)} status={summary.payment_vs_bank_status} />
            <Figure label="Roll-forward" value={statusText(summary.roll_forward_status)} status={summary.roll_forward_status} />
            <Figure label="Temuan kualitas data" value={summary.data_quality_findings} />
          </div>
          {summary.taxpayer_scope && (
            <div className="text-xs text-amber-800 px-1 pt-2" data-testid="wht-scope-note">
              {summary.taxpayer_scope.other_taxpayer} bukti pembayaran atas nama wajib pajak lain
              dikeluarkan dari populasi entitas. {summary.taxpayer_scope.basis}
            </div>
          )}
          {summary.records_with_a_computed_tax_amount > 0 && (
            <div className="text-xs text-amber-800 px-1 pt-1" data-testid="wht-computed-note">
              {summary.records_with_a_computed_tax_amount} catatan tidak memuat nilai pajak pada
              sumbernya; nilai dihitung dari dasar pengenaan dan tarif.
            </div>
          )}
        </div>
      )}

      <div className="bg-white border border-border rounded-sm">
        <div className="flex flex-wrap gap-1 border-b border-border px-2 py-1">
          {available.map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)} data-testid={`wht-tab-${key}`}
              className={`text-xs px-2 py-1 rounded-sm ${tab === key ? "bg-slate-200 font-medium" : "hover:bg-slate-100"}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="overflow-x-auto p-2" data-testid={`wht-panel-${tab}`}>
          {tab === "ringkasan" && <Sources rows={view.sources} mapping={view.account_mapping} />}
          {tab === "populasi" && <Population population={view.population} byArticle={view.by_article} />}
          {tab === "pph21" && <Article name="PPh 21" data={(view.by_article || {})["PPh 21"]} branches={view.pph21_branches} />}
          {tab === "pph23" && <Article name="PPh 23" data={(view.by_article || {})["PPh 23"]} />}
          {tab === "pphfinal" && <Article name="PPh 4(2)" data={(view.by_article || {})["PPh 4(2)"]} />}
          {tab === "payroll" && <Payroll branches={view.pph21_branches} overlap={view.pph21_overlap} payroll={view.population?.payroll} />}
          {tab === "vsgl" && <Ledger result={view.wht_vs_gl} />}
          {tab === "dpp" && <SourceTransactions result={view.source_transactions} />}
          {tab === "billing" && <Chain result={view.payable_vs_billing} origin="billing" />}
          {tab === "pembayaran" && <Chain result={view.billing_vs_payment} origin="payment" />}
          {tab === "bank" && <BankChain result={view.payment_vs_bank} />}
          {tab === "rollforward" && <RollForward result={view.roll_forward} />}
          {tab === "kualitas" && <Quality result={view.data_quality} />}
          {tab === "bukti" && <Evidence rows={view.evidence} />}
        </div>
      </div>
    </div>
  );
}

function statusText(status) {
  if (status === "SOURCE_NOT_SUPPLIED") return NOT_AVAILABLE;
  return ITEM_LABELS[status] || status;
}

function Figure({ label, value, status }) {
  return (
    <div className="border border-border rounded-sm p-2">
      <div className="text-muted-foreground">{label}</div>
      <div className={`font-medium tabular-nums ${status ? `inline-block mt-1 px-2 py-0.5 border rounded-sm ${STATUS_STYLE[status] || ""}` : ""}`}>
        {value ?? "—"}
      </div>
    </div>
  );
}

function Sources({ rows = [], mapping }) {
  return (
    <div className="space-y-3">
      <table className="w-full audit-table text-xs" data-testid="wht-sources">
        <thead><tr><th>Arsip</th><th className="text-right">Berkas</th><th className="text-right">Catatan</th>
          <th className="text-right">Tidak terbaca</th><th>Jenis berkas</th><th>Catatan sumber</th></tr></thead>
        <tbody>{rows.map((row) => (
          <tr key={row.id}>
            <td>{row.filename}</td>
            <td className="text-right tabular-nums">{row.members}</td>
            <td className="text-right tabular-nums">{row.rows}</td>
            <td className="text-right tabular-nums">{row.unreadable_members}</td>
            <td className="max-w-sm">{Object.entries(row.member_types || {}).map(([k, v]) => `${k}:${v}`).join(", ")}</td>
            <td className="max-w-md">{(row.notes || []).join(" ")}</td>
          </tr>))}
        </tbody>
      </table>
      <div className="text-xs" data-testid="wht-mapping">
        <div className="font-medium mb-1">Pemetaan akun pajak</div>
        {Object.entries(mapping?.payable_accounts || {}).map(([article, code]) => (
          <div key={article}>{article}: {code}</div>))}
        {!Object.keys(mapping?.payable_accounts || {}).length && <div>belum disetujui</div>}
      </div>
    </div>
  );
}

function Population({ population, byArticle }) {
  const controls = population?.controls || {};
  return (
    <div className="space-y-2 text-xs" data-testid="wht-population">
      <table className="w-full audit-table text-xs">
        <thead><tr><th>Status populasi</th><th className="text-right">Jumlah</th></tr></thead>
        <tbody>{Object.entries(controls.by_status || {}).map(([status, count]) => (
          <tr key={status}><td>{ITEM_LABELS[status] || status}</td>
            <td className="text-right tabular-nums">{count}</td></tr>))}
        </tbody>
      </table>
      <table className="w-full audit-table text-xs">
        <thead><tr><th>Pasal</th><th className="text-right">Catatan</th><th className="text-right">Dasar</th>
          <th className="text-right">Pajak</th></tr></thead>
        <tbody>{Object.entries(byArticle || {}).map(([article, data]) => (
          <tr key={article}><td>{article}</td>
            <td className="text-right tabular-nums">{data.records}</td>
            <td className="text-right tabular-nums">{rupiah(data.tax_base)}</td>
            <td className="text-right tabular-nums">{rupiah(data.tax)}</td></tr>))}
        </tbody>
      </table>
    </div>
  );
}

function Article({ name, data, branches }) {
  if (!data) return <div className="text-xs text-muted-foreground p-2">{NOT_AVAILABLE}</div>;
  return (
    <div className="space-y-2 text-xs" data-testid={`wht-article-${name.replace(/\W/g, "")}`}>
      <table className="w-full audit-table text-xs">
        <tbody>
          <tr><td>Catatan</td><td className="text-right tabular-nums">{data.records}</td></tr>
          <tr><td>Dasar pengenaan pajak</td><td className="text-right tabular-nums">{rupiah(data.tax_base)}</td></tr>
          <tr><td>Pajak dipotong</td><td className="text-right tabular-nums">{rupiah(data.tax)}</td></tr>
        </tbody>
      </table>
      {branches && (
        <div className="text-amber-800" data-testid="wht-pph21-split">
          Bukti potong tidak mencakup seluruh PPh 21; penggajian melengkapinya. Keduanya dijaga
          terpisah dan hanya bertemu pada akun hutang pajak.
        </div>)}
    </div>
  );
}

function Payroll({ branches, overlap = [], payroll }) {
  return (
    <div className="space-y-2 text-xs" data-testid="wht-payroll">
      <table className="w-full audit-table text-xs">
        <tbody>
          <tr><td>Baris penggajian dibaca</td><td className="text-right tabular-nums">{payroll?.total}</td></tr>
          <tr><td>Duplikat antar arsip dihapus</td><td className="text-right tabular-nums">{(payroll?.by_status || {}).DUPLICATE ?? 0}</td></tr>
          <tr><td>Baris penggajian aktif</td><td className="text-right tabular-nums">{branches?.PAYROLL?.count}</td></tr>
          <tr><td>Penghasilan bruto</td><td className="text-right tabular-nums">{rupiah(branches?.PAYROLL?.gross)}</td></tr>
          <tr><td>PPh 21 penggajian</td><td className="text-right tabular-nums">{rupiah(branches?.PAYROLL?.tax)}</td></tr>
          <tr><td>PPh 21 bukti potong</td><td className="text-right tabular-nums">{rupiah(branches?.BUPOT?.tax)}</td></tr>
          <tr><td>Gabungan (tanpa perhitungan ganda)</td><td className="text-right tabular-nums">{rupiah(branches?.combined_tax)}</td></tr>
        </tbody>
      </table>
      <div>{overlap.length} orang muncul pada kedua sumber; nilainya tidak dijumlahkan dua kali.</div>
    </div>
  );
}

function Ledger({ result }) {
  if (!result) return <div className="text-xs text-muted-foreground p-2" data-testid="wht-gl-empty">{NOT_AVAILABLE}</div>;
  return (
    <table className="w-full audit-table text-xs" data-testid="wht-gl">
      <thead><tr><th>Pasal</th><th>Masa</th><th className="text-right">WHT</th>
        <th className="text-right">Kredit buku besar</th><th className="text-right">Selisih</th>
        <th>Status</th><th>Simpulan auditor</th></tr></thead>
      <tbody>{(result.items || []).slice(0, 200).map((row, index) => (
        <tr key={index}>
          <td>{row.article}</td><td>{row.period}</td>
          <td className="text-right tabular-nums">{rupiah(row.withholding)}</td>
          <td className="text-right tabular-nums">{rupiah(row.ledger_credit)}</td>
          <td className="text-right tabular-nums">{rupiah(row.difference)}</td>
          <td>{ITEM_LABELS[row.status] || row.status}</td>
          <td className="text-muted-foreground">Belum disimpulkan</td>
        </tr>))}
      </tbody>
    </table>
  );
}

function SourceTransactions({ result }) {
  if (!result) return <div className="text-xs text-muted-foreground p-2" data-testid="wht-dpp-empty">{NOT_AVAILABLE}</div>;
  return (
    <div className="space-y-2 text-xs" data-testid="wht-dpp">
      <div className="text-muted-foreground">{result.basis}</div>
      <div className="text-amber-800" data-testid="wht-dpp-coverage">{result.coverage_note}</div>
      <table className="w-full audit-table text-xs">
        <thead><tr><th>Pasal</th><th>Masa</th><th>Lawan transaksi</th><th>Referensi</th>
          <th className="text-right">DPP</th><th className="text-right">Pajak</th>
          <th>Status</th><th>Bukti</th></tr></thead>
        <tbody>{(result.items || []).slice(0, 200).map((row, index) => (
          <tr key={index}>
            <td>{row.article}</td><td>{row.period}</td><td>{row.counterparty}</td>
            <td>{row.matched_reference || row.document_number || "—"}</td>
            <td className="text-right tabular-nums">{rupiah(row.tax_base)}</td>
            <td className="text-right tabular-nums">{rupiah(row.tax_amount)}</td>
            <td>{ITEM_LABELS[row.status] || row.status}</td>
            <td>{(row.evidence || []).join(", ")}</td>
          </tr>))}
        </tbody>
      </table>
      <div>{result.transactions_without_withholding} transaksi pembelian tanpa pemotongan pajak.</div>
    </div>
  );
}

function Chain({ result, origin }) {
  if (!result) return <div className="text-xs text-muted-foreground p-2" data-testid={`wht-${origin}-empty`}>{NOT_AVAILABLE}</div>;
  return (
    <table className="w-full audit-table text-xs" data-testid={`wht-${origin}`}>
      <thead><tr><th>{origin === "billing" ? "Jenis pajak" : "Kode billing"}</th>
        <th>{origin === "billing" ? "Masa" : "Tanggal bayar"}</th>
        <th className="text-right">Nilai</th><th className="text-right">Pembanding</th>
        <th className="text-right">Selisih</th><th>Status</th></tr></thead>
      <tbody>{(result.items || []).slice(0, 200).map((row, index) => (
        <tr key={index}>
          <td>{origin === "billing" ? row.tax_type : row.billing_code}</td>
          <td>{origin === "billing" ? row.tax_period : (row.payment_dates || []).join(", ")}</td>
          <td className="text-right tabular-nums">{rupiah(row.amount)}</td>
          <td className="text-right tabular-nums">{rupiah(origin === "billing" ? row.billed_amount : row.paid_amount)}</td>
          <td className="text-right tabular-nums">{rupiah(row.difference)}</td>
          <td>{ITEM_LABELS[row.status] || row.status}</td>
        </tr>))}
      </tbody>
    </table>
  );
}

function BankChain({ result }) {
  if (!result) return <div className="text-xs text-muted-foreground p-2" data-testid="wht-bank-empty">{NOT_AVAILABLE}</div>;
  return (
    <div className="space-y-2 text-xs" data-testid="wht-bank">
      <div className="text-muted-foreground">{result.basis}</div>
      <table className="w-full audit-table text-xs">
        <thead><tr><th>Kode billing</th><th className="text-right">Nilai</th><th>Tanggal bayar</th>
          <th>Baris bank</th><th>Status</th><th>Bukti</th></tr></thead>
        <tbody>{(result.items || []).slice(0, 200).map((row, index) => (
          <tr key={index}>
            <td>{row.billing_code}</td>
            <td className="text-right tabular-nums">{rupiah(row.amount)}</td>
            <td>{row.payment_date}</td><td>{row.bank_row || "—"}</td>
            <td>{ITEM_LABELS[row.status] || row.status}</td>
            <td>{(row.evidence || []).join(", ")}</td>
          </tr>))}
        </tbody>
      </table>
    </div>
  );
}

function RollForward({ result }) {
  if (!result) return <div className="text-xs text-muted-foreground p-2" data-testid="wht-rollforward-empty">{NOT_AVAILABLE}</div>;
  return (
    <div className="space-y-3 text-xs" data-testid="wht-rollforward">
      <div className="text-muted-foreground">{result.basis}</div>
      {Object.entries(result.articles || {}).map(([article, data]) => (
        <div key={article}>
          <div className="font-medium mb-1">{article}</div>
          <table className="w-full audit-table text-xs">
            <thead><tr><th>Masa</th><th className="text-right">Saldo awal</th>
              <th className="text-right">WHT sumber</th><th className="text-right">Diakui GL</th>
              <th className="text-right">Dilunasi GL</th><th className="text-right">Saldo akhir</th>
              <th className="text-right">Selisih bulan</th><th className="text-right">Selisih kumulatif</th>
              <th>Simpulan auditor</th></tr></thead>
            <tbody>{(data.months || []).map((m) => (
              <tr key={m.period}>
                <td>{m.period}</td>
                <td className="text-right tabular-nums">{rupiah(m.opening)}</td>
                <td className="text-right tabular-nums">{rupiah(m.withholding_per_source)}</td>
                <td className="text-right tabular-nums">{rupiah(m.recognised_in_ledger)}</td>
                <td className="text-right tabular-nums">{rupiah(m.settled_in_ledger)}</td>
                <td className="text-right tabular-nums">{rupiah(m.closing)}</td>
                <td className="text-right tabular-nums">{rupiah(m.difference_this_month)}</td>
                <td className="text-right tabular-nums">{rupiah(m.difference_carried_forward)}</td>
                <td className="text-muted-foreground">Belum disimpulkan</td>
              </tr>))}
            </tbody>
          </table>
        </div>))}
    </div>
  );
}

function Quality({ result }) {
  if (!result) return <div className="text-xs text-muted-foreground p-2">{NOT_AVAILABLE}</div>;
  return (
    <div className="space-y-2 text-xs" data-testid="wht-quality">
      <div className="text-muted-foreground">{result.note}</div>
      <table className="w-full audit-table text-xs">
        <thead><tr><th>Temuan</th><th className="text-right">Jumlah</th></tr></thead>
        <tbody>{Object.entries(result.findings || {}).map(([name, count]) => (
          <tr key={name}><td>{name}</td><td className="text-right tabular-nums">{count}</td></tr>))}
        </tbody>
      </table>
    </div>
  );
}

function Evidence({ rows = [] }) {
  return (
    <table className="w-full audit-table text-xs" data-testid="wht-evidence">
      <thead><tr><th>Arsip</th><th>Jenis</th><th>SHA-256</th><th>Masa</th><th>Diperoleh</th></tr></thead>
      <tbody>{rows.map((row, index) => (
        <tr key={index}>
          <td>{row.filename}</td><td>{row.source_type}</td>
          <td className="font-mono">{(row.sha256 || "").slice(0, 16)}…</td>
          <td>{row.period || "—"}</td><td>{row.obtained_at}</td>
        </tr>))}
      </tbody>
    </table>
  );
}
